import React, { useState, useEffect, Fragment } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3,
  Plus,
  Activity,
  CheckCircle,
  AlertCircle,
  Coins,
  Target,
  Crown,
  Briefcase,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  DollarSign,
  Percent,
  Search,
  Bell,
  Settings,
  SortAsc,
  SortDesc
} from 'lucide-react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;

// Helper function to calculate performance score
const calculatePerformanceScore = (criteria) => {
  if (!criteria) return 0;
  const totalCriteria = Object.keys(criteria).length;
  const metCriteria = Object.values(criteria).filter(Boolean).length;
  return Math.round((metCriteria / totalCriteria) * 100);
};

// Helper function to get parsed financial criteria
const getParsedFinancialCriteria = (stock) => {
  try {
    return typeof stock.financialCriteria === 'string' 
      ? JSON.parse(stock.financialCriteria) 
      : stock.financial_criteria || stock.financialCriteria || {};
  } catch (error) {
    console.error('Error parsing financialCriteria for sorting:', error);
    return stock.financial_criteria || {};
  }
};

const StockTable = ({ stocks, type = "" }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'performance_score', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Helper function to toggle row expansion (only one row at a time)
  const toggleRowExpansion = (stockKey) => {
    if (expandedRows.has(stockKey)) {
      // If clicking on already expanded row, close it
      setExpandedRows(new Set());
    } else {
      // If clicking on a different row, close all others and open this one
      setExpandedRows(new Set([stockKey]));
    }
  };

  // Helper function to extract financial criteria values
  const getFinancialValue = (stock, key) => {
    const financialCriteria = getParsedFinancialCriteria(stock);
    
    switch (key) {
      case 'sales_growth_5y':
        return financialCriteria?.sales_growth_5y_50 ? '✓' : '✗';
      case 'sales_growth_1y':
        return financialCriteria?.sales_growth_1y_5 ? '✓' : '✗';
      case 'earnings_growth_1y':
        return financialCriteria?.earnings_growth_1y_5 ? '✓' : '✗';
      case 'roe':
        return financialCriteria?.roe_15 ? '✓' : '✗';
      case 'peg_ratio':
        return financialCriteria?.peg_ratio_0_1 ? '✓' : '✗';
      case 'debt_equity':
        return financialCriteria?.debt_equity_0_1 ? '✓' : '✗';
      case 'free_cash_flow':
        return financialCriteria?.free_cash_flow_positive ? '✓' : '✗';
      default:
        return 'N/A';
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedStocks = () => {
    const sortableStocks = [...stocks];
    
    sortableStocks.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'performance_score':
          const financialCriteriaA = getParsedFinancialCriteria(a);
          const financialCriteriaB = getParsedFinancialCriteria(b);
          aValue = a.performance_score || calculatePerformanceScore(financialCriteriaA) || 0;
          bValue = b.performance_score || calculatePerformanceScore(financialCriteriaB) || 0;
          break;
        case 'symbol':
          aValue = a.symbol || '';
          bValue = b.symbol || '';
          break;
        case 'company_name':
          aValue = a.company_name || '';
          bValue = b.company_name || '';
          break;
        case 'sector':
          aValue = a.sector || '';
          bValue = b.sector || '';
          break;
        default:
          aValue = getFinancialValue(a, sortConfig.key);
          bValue = getFinancialValue(b, sortConfig.key);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortableStocks;
  };

  const SortableHeader = ({ column, children }) => (
    <th 
      className="sortable-header" 
      onClick={() => handleSort(column)}
    >
      <div className="header-content">
        {children}
        {sortConfig.key === column ? (
          sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
        ) : (
          <div className="w-4 h-4"></div>
        )}
      </div>
    </th>
  );

  const getPerformanceScoreClass = (score) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 75) return 'score-good';
    if (score >= 60) return 'score-average';
    return 'score-poor';
  };

  const getCriteriaClass = (value) => {
    return value === '✓' ? 'criteria-met' : 'criteria-not-met';
  };

  return (
    <div className="stock-table-container">
      <div className="table-header">
        <h2 className="table-title">
          {type === 'portfolio' ? (
            <>
              <span className="flag-icon">🇮🇳</span>
              Indian Stocks ({stocks.length})
            </>
          ) : (
            <>
              <span className="flag-icon">🇺🇸</span>
              US Stocks ({stocks.length})
            </>
          )}
        </h2>
      </div>
      
      <div className="table-wrapper">
        <table className="stock-table">
          <thead>
            <tr>
              <SortableHeader column="symbol">Symbol</SortableHeader>
              <SortableHeader column="company_name">Company</SortableHeader>
              <SortableHeader column="sector">Sector</SortableHeader>
              <SortableHeader column="performance_score">Score</SortableHeader>
              <SortableHeader column="sales_growth_5y">5Y Sales</SortableHeader>
              <SortableHeader column="sales_growth_1y">1Y Sales</SortableHeader>
              <SortableHeader column="earnings_growth_1y">1Y Earnings</SortableHeader>
              <SortableHeader column="roe">ROE</SortableHeader>
              <SortableHeader column="peg_ratio">PEG</SortableHeader>
              <SortableHeader column="debt_equity">D/E</SortableHeader>
              <SortableHeader column="free_cash_flow">FCF</SortableHeader>
            </tr>
          </thead>
          <tbody>
            {getSortedStocks().map((stock, index) => {
              const financialCriteria = getParsedFinancialCriteria(stock);
              const performanceScore = stock.performance_score || calculatePerformanceScore(financialCriteria) || 0;
              const stockKey = stock.symbol || stock.company_name || index;
              const isExpanded = expandedRows.has(stockKey);
              
              // Parse JSON strings for latestNews
              const latestNews = (() => {
                try {
                  return typeof stock.latestNews === 'string' ? JSON.parse(stock.latestNews) : stock.latestNews || [];
                } catch (error) {
                  console.error('Error parsing latestNews:', error);
                  return [];
                }
              })();

              return (
                <Fragment key={stockKey}>
                  <tr 
                    className={`stock-row ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleRowExpansion(stockKey)}
                  >
                    <td className="symbol-cell">
                      <div className="symbol-container">
                        <span className="symbol">{stock.symbol || 'N/A'}</span>
                        <div className="expand-indicator">
                          {isExpanded ? '▼' : '▶'}
                        </div>
                      </div>
                    </td>
                    <td className="company-cell">
                      <div className="company-name">{stock.company_name || 'N/A'}</div>
                    </td>
                    <td className="sector-cell">
                      <span className="sector-badge">{stock.sector || 'N/A'}</span>
                    </td>
                    <td className="score-cell">
                      <span className={`performance-score ${getPerformanceScoreClass(performanceScore)}`}>
                        {performanceScore}%
                      </span>
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'sales_growth_5y'))}`}>
                      {getFinancialValue(stock, 'sales_growth_5y')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'sales_growth_1y'))}`}>
                      {getFinancialValue(stock, 'sales_growth_1y')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'earnings_growth_1y'))}`}>
                      {getFinancialValue(stock, 'earnings_growth_1y')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'roe'))}`}>
                      {getFinancialValue(stock, 'roe')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'peg_ratio'))}`}>
                      {getFinancialValue(stock, 'peg_ratio')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'debt_equity'))}`}>
                      {getFinancialValue(stock, 'debt_equity')}
                    </td>
                    <td className={`criteria-cell ${getCriteriaClass(getFinancialValue(stock, 'free_cash_flow'))}`}>
                      {getFinancialValue(stock, 'free_cash_flow')}
                    </td>
                  </tr>
                  
                  {/* Expanded Row Content */}
                  {isExpanded && (
                    <tr className="expanded-content-row">
                      <td colSpan="11" className="expanded-content-cell">
                        <div className="expanded-content">
                          <div className="expanded-grid">
                            {/* Valuation Insight Section */}
                            {stock.valuation_insight && stock.valuation_insight.trim() && (
                              <div className="insight-section">
                                <div className="section-header">
                                  <Target className="w-5 h-5" />
                                  <h3>Valuation Insight</h3>
                                </div>
                                <div className="insight-content">
                                  <p>{stock.valuation_insight}</p>
                                </div>
                              </div>
                            )}

                            {/* Latest News Section */}
                            {latestNews && latestNews.length > 0 && (
                              <div className="news-section">
                                <div className="section-header">
                                  <Bell className="w-5 h-5" />
                                  <h3>Latest News</h3>
                                </div>
                                <div className="news-content">
                                  {latestNews.slice(0, 4).map((news, newsIndex) => (
                                    <div key={newsIndex} className="news-item">
                                      <div className="news-headline">{news.headline}</div>
                                      <div className="news-date">{news.date}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Show message if no additional data */}
                            {(!stock.valuation_insight || !stock.valuation_insight.trim()) && 
                             (!latestNews || latestNews.length === 0) && (
                              <div className="no-additional-data">
                                <div className="no-data-message">
                                  <AlertCircle className="w-5 h-5" />
                                  <p>No additional information available for this stock</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {stocks.length === 0 && (
        <div className="no-data-message">
          <p>No stocks found</p>
        </div>
      )}
    </div>
  );
};

const MarketIndexCard = ({ title, value, change, high52w }) => {
  const formatValue = (val) => {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  };

  const formatChange = (changeVal) => {
    if (typeof changeVal === 'number') {
      return changeVal.toFixed(2);
    }
    return changeVal;
  };

  return (
    <div className="market-index-card">
      <div className="index-header">
        <h3>{title}</h3>
        {/* {high52w && <div className="ratio-badge">52W High: {high52w}</div>} */}
      </div>
      {/* <div className="index-value">{formatValue(value)}</div> */}
      <div className={`index-change ${change >= 0 ? 'positive' : 'negative'}`}>
        {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {change >= 0 ? '+' : ''}{formatChange(change)}%
      </div>
    </div>
  );
};

const EquityGoldWidget = ({ ratioData }) => {
  if (!ratioData || !ratioData.summary) return null;

  const { ratio, change, trend, recommendation } = ratioData.summary;

  const getSignalColor = (changeValue) => {
    const changeNum = parseFloat(changeValue.replace('%', ''));
    if (changeNum >= 0) return 'signal-buy';
    if (changeNum >= -1) return 'signal-hold';
    return 'signal-sell';
  };

  const getTrendColor = (trendText) => {
    if (trendText && trendText.toLowerCase().includes('bullish')) return 'positive';
    if (trendText && trendText.toLowerCase().includes('bearish')) return 'negative';
    return 'neutral';
  };

  return (
    <div className="market-index-card">
      <div className="index-header">
        <h3>
          <Coins className="w-4 h-4 inline mr-1" />
          Sensex/Gold : {ratio.toFixed(2)}
        </h3>
        {/* <div className="ratio-badge">{change}</div> */}
      </div>
      {/* <div className="index-value">{ratio.toFixed(2)}</div> */}
      <div className={`index-change ${getTrendColor(trend)}`}>
        {trend && trend.toLowerCase().includes('bullish') ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {trend}
      </div>
    </div>
  );
};

const MarketAlerts = () => (
  <div className="market-alerts">
    <div className="alerts-header">
      <Bell className="w-5 h-5" />
      Market Alerts
    </div>
    <div className="alert-item stable">
      <Activity className="w-4 h-4" />
      Markets Stable
    </div>
  </div>
);

const Dashboard = () => {
  const [portfolioStocks, setPortfolioStocks] = useState([]);
  const [watchlistStocks, setWatchlistStocks] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [marketIndices, setMarketIndices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [equityGoldRatio, setEquityGoldRatio] = useState(null);
  const [activeTab, setActiveTab] = useState('portfolio');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    console.log('Starting to load data from:', API);
    
    // Load portfolio stocks
    try {
      const portfolioRes = await axios.get(`${API}/stocks/watchlist`);
      console.log('Portfolio response:', portfolioRes);
      
      // Extract scrapedData from the response
      const portfolioData = portfolioRes.data?.results || [];
      console.log('Portfolio loaded:', portfolioData.length, 'items');
      setPortfolioStocks(portfolioData);
    } catch (error) {
      console.error('Error loading portfolio:', error);
      setPortfolioStocks([]);
    }

    // Load watchlist stocks
    try {
      const watchlistRes = await axios.get(`${API}/stocks/us-watchlist`);
      const watchlistResData = watchlistRes.data?.results || [];
      console.log('Watchlist loaded:', watchlistResData.length, 'items');
      setWatchlistStocks(watchlistResData);
    } catch (error) {
      console.error('Error loading watchlist:', error);
      setWatchlistStocks([]);
    }

    // Load all stocks
    try {
      // const allStocksRes = await axios.get(`${API}/stocks/all`);
      const allStocksRes = []
      // console.log('All stocks loaded:', allStocksRes.data?.length, 'items');
      setAllStocks(allStocksRes.data || []);
    } catch (error) {
      console.error('Error loading all stocks:', error);
      setAllStocks([]);
    }

    // Load equity/gold ratio
    try {
      const ratioRes = await axios.get(`${API}/equity-gold-ratio`);
      console.log('Equity/Gold ratio loaded:', ratioRes.data);
      setEquityGoldRatio(ratioRes.data);
    } catch (error) {
      console.error('Error loading equity/gold ratio:', error);
      setEquityGoldRatio(null);
    }

    // Load market indices
    try {
      const indicesRes = await axios.get(`${API}/market-indices`);
      console.log('Market indices loaded:', indicesRes.data);
      
      // Transform the response to match our component structure
      const indicesData = [];
      if (indicesRes.data.summary) {
        const { nifty50, niftyMidcap150, niftySmallcap100 } = indicesRes.data.summary;
        
        if (nifty50) {
          indicesData.push({
            name: "Nifty 50",
            value: nifty50.price,
            change: parseFloat(nifty50.change.replace('%', '')),
            high52w: nifty50.high52w
          });
        }
        
        if (niftyMidcap150) {
          indicesData.push({
            name: "Nifty Midcap 150",
            value: niftyMidcap150.price,
            change: parseFloat(niftyMidcap150.change.replace('%', '')),
            high52w: niftyMidcap150.high52w
          });
        }
        
        if (niftySmallcap100) {
          indicesData.push({
            name: "Nifty Smallcap 100",
            value: niftySmallcap100.price,
            change: parseFloat(niftySmallcap100.change.replace('%', '')),
            high52w: niftySmallcap100.high52w
          });
        }
      }
      
      setMarketIndices(indicesData);
    } catch (error) {
      console.error('Error loading market indices:', error);
      setMarketIndices([]);
    }

    setLoading(false);
    console.log('All data loading completed');
  };

  const searchStocks = async () => {
    if (!searchTerm.trim()) {
      loadInitialData();
      return;
    }

    try {
      const response = await axios.get(`${API}/stocks/search?q=${searchTerm}`);
      setAllStocks(response.data);
    } catch (error) {
      console.error('Error searching stocks:', error);
    }
  };

  return (
    <div className="modern-app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="app-branding">
            <div className="app-icon">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div className="app-title">
              <h1>AI Stock Analysis</h1>
              <p>Stock Market Intelligence</p>
            </div>
          </div>

        </div>
      </header>

      {/* Market Indices */}
      <section className="market-indices">
        {marketIndices.slice(0, 4).map((index, idx) => (
          <MarketIndexCard 
            key={idx}
            title={index.name} 
            value={index.value} 
            change={index.change}
            high52w={index.high52w}
          />
        ))}
        {marketIndices.length === 0 && !loading && (
          <div className="market-index-card placeholder">
            <div className="index-header">
              <h3>Loading Indices...</h3>
            </div>
            <div className="index-value">--</div>
            <div className="index-change">--</div>
          </div>
        )}
        
        {/* Equity/Gold Ratio Widget - placed next to indices */}
        <EquityGoldWidget ratioData={equityGoldRatio} />
      </section>

      {/* Market Alerts */}
      {/* <section className="alerts-section">
        <MarketAlerts />
      </section> */}

      {/* Navigation Tabs */}
      <nav className="nav-tabs">
        <button 
          className={`nav-tab ${activeTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          <span className="flag-icon">🇮🇳</span>
          <Briefcase className="w-4 h-4" />
          Indian Stocks ({portfolioStocks.length})
        </button>
        <button 
          className={`nav-tab ${activeTab === 'watchlist' ? 'active' : ''}`}
          onClick={() => setActiveTab('watchlist')}
        >
          <span className="flag-icon">🇺🇸</span>
          <Eye className="w-4 h-4" />
          US Stocks ({watchlistStocks.length})
        </button>
        {/* 
        <button 
          className={`nav-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <BarChart3 className="w-4 h-4" />
          All Stocks ({allStocks.length})
        </button> */}
      </nav>

      {/* Stock Lists */}
      <main className="stock-lists">
        {loading && (
          <div className="loading-container">
            <p>Loading data...</p>
          </div>
        )}

        {activeTab === 'portfolio' && !loading && (
          <StockTable stocks={portfolioStocks} type="portfolio" />
        )}

        {activeTab === 'watchlist' && !loading && (
          <StockTable stocks={watchlistStocks} type="watchlist" />
        )}

        {activeTab === 'all' && !loading && (
          <StockTable stocks={allStocks} type="all" />
        )}
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Dashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;